import { browser } from '$app/environment';

export type Theme = 'light' | 'dark';

const STORAGE_KEY = 'theme';

/**
 * Reads the explicitly-chosen theme from localStorage.
 * Returns null when the user has never made a manual choice (so we can
 * fall back to the OS preference).
 */
export function getStoredTheme(): Theme | null {
  if (browser) {
    const value = localStorage.getItem(STORAGE_KEY);
    return value === 'light' || value === 'dark' ? value : null;
  }
  return null;
}

/**
 * Resolves the theme to use: an explicit stored choice if present,
 * otherwise the OS `prefers-color-scheme` setting (defaulting to light).
 */
export function getPreferredTheme(): Theme {
  const stored = getStoredTheme();
  if (stored) {
    return stored;
  }
  if (browser && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

/**
 * Applies the theme to the document by toggling the `.dark` class on <html>,
 * which is what the design-system's `dark` variant keys off.
 */
export function applyTheme(theme: Theme): void {
  if (browser) {
    document.documentElement.classList.toggle('dark', theme === 'dark');
  }
}

/**
 * Persists the user's manual theme choice and applies it immediately.
 */
export function setTheme(theme: Theme): void {
  if (browser) {
    localStorage.setItem(STORAGE_KEY, theme);
  }
  applyTheme(theme);
}
